/**
 * Privacy Policy (V1 auth-profiles-gdpr — S-27 About/legal). Static, serious.
 * Mirrors the GDPR posture actually implemented: minimal PII, coarse
 * location, consent-based geolocation, export, grace-period deletion.
 */
import { Link } from "react-router-dom";
import { LegalPage } from "./legal-page.js";

const EFFECTIVE = "2026-07-01";

export function PrivacyScreen() {
  return (
    <LegalPage title="Privacy Policy">
      <p className="text-dim">Effective {EFFECTIVE}.</p>

      <h2>1. What we collect</h2>
      <ul style={{ paddingLeft: 20 }}>
        <li>
          <strong>Account</strong>: email, password hash (Argon2 — never the password), date of
          birth (age gate), timezone.
        </li>
        <li>
          <strong>Profile</strong>: what you choose to publish — photos, bio, prompts, vitals,
          interests, gender, pronouns, "show me" preferences.
        </li>
        <li>
          <strong>Location</strong>: only with your explicit consent, and stored coarsened to
          roughly 1 km (a geohash centroid) — your exact coordinates are never persisted. You
          can instead pick a city manually, or revoke GPS consent at any time.
        </li>
        <li>
          <strong>Activity</strong>: swipes, matches, messages, calls metadata (never call
          content), purchases, and a security event log.
        </li>
        <li>
          <strong>Device</strong>: a random device identifier and hashed fingerprints used for
          abuse prevention (rate limits, underage locks, device bans).
        </li>
      </ul>

      <h2>2. What we use it for</h2>
      <p>
        Running the service (showing profiles nearby, delivering messages and notifications),
        safety (moderation of uploads, report handling, abuse prevention), and payments
        (processed by Stripe — we never see card numbers). We do not sell personal data, run
        third-party advertising, or track you across other sites. Analytics are first-party and
        aggregate.
      </p>

      <h2>3. Who can see what</h2>
      <p>
        Your profile is visible to users who fit your mutual "show me" settings. Rejections are
        not secret on Humble: users you reject may see that you rejected them — this is
        disclosed during onboarding, before your first swipe. A Super Reject is the exception:
        its sender is never revealed to the target, anywhere, including in data exports.
      </p>

      <h2>4. Your rights (GDPR)</h2>
      <ul style={{ paddingLeft: 20 }}>
        <li>
          <strong>Access & portability</strong>: request a machine-readable export of your data
          from your profile page. The export contains your own data only — other people's
          identities are pseudonymized where the law requires it.
        </li>
        <li>
          <strong>Erasure</strong>: delete your account from your profile page. A 14-day grace
          period lets you cancel; afterwards profile, photos, messages and history are
          permanently erased. Moderation evidence we are legally required to keep is retained
          for up to 90 days, then destroyed.
        </li>
        <li>
          <strong>Rectification</strong>: everything on your profile is editable in place.
        </li>
        <li>
          <strong>Consent withdrawal</strong>: location consent is revocable in Settings;
          notification categories are individually switchable (safety notices excepted).
        </li>
        <li>
          <strong>Complaint</strong>: you may lodge a complaint with your supervisory authority
          (in France, the CNIL).
        </li>
      </ul>

      <h2>5. Retention</h2>
      <p>
        Unverified accounts are deleted after 7 days; abandoned onboarding after 90 days of
        inactivity. Active account data is kept while the account exists. Deleted accounts are
        purged after the 14-day grace. Backups rotate on a 14-day schedule.
      </p>

      <h2>6. Security</h2>
      <p>
        Passwords are hashed with Argon2; sensitive identifiers are stored hashed; transport is
        TLS-only; access tokens are short-lived; media access is authenticated per request.
        Report a vulnerability: security@humble.ekaii.fr.
      </p>

      <h2>7. Contact</h2>
      <p>
        Data controller: Humble (humble.ekaii.fr). Privacy requests:{" "}
        <a href="mailto:privacy@humble.ekaii.fr">privacy@humble.ekaii.fr</a> · See also the{" "}
        <Link to="/safety">Safety Center</Link> and <Link to="/legal/terms">Terms</Link>.
      </p>
    </LegalPage>
  );
}
